/**
 * Driver ErpService — a lean Bearer adapter for the external Driver ePickup
 * app. It hits the same-origin `/api/driver/*` Bearer endpoints (Caddy proxies
 * `/api` to the Core API). Bearer, NOT cookie: there is no
 * `credentials: 'include'`, and the token comes from the PKCE auth module.
 *
 * This is NOT a `@toeverything/infra` DI service — the driver app is a separate,
 * lean origin and does not pull `@affine/core`.
 */

/**
 * The Driver redact projection (Task 11): the external serializer keeps
 * `assigned_to` (the dispatch key) but omits internal provenance/lifecycle. The
 * server computes `can_complete` (the 4B.6 ACT-authority promotion) so the
 * client never re-derives whether a pickup can be completed.
 */
export interface DriverPickup {
  id: string;
  type: string;
  title: string | null;
  title_redacted: boolean;
  data: Record<string, unknown>;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  can_complete: boolean;
}

export interface Geo {
  lat: number;
  lng: number;
  accuracy_m?: number;
}

export interface PickupCompleteBody {
  client_op_id: string;
  releasing_signature_png_b64: string;
  agent_signature_png_b64: string;
  logistics_signature_png_b64: string;
  agent_employee_id: string;
  net_weight_kg?: number;
  net_weight_override_reason?: string;
  geo?: Geo;
}

export interface PickupCompleteResult {
  order_id: string;
  custody_event_id: string;
  status: string;
}

export interface PickupDetail {
  order: DriverPickup;
  children: Record<string, unknown>[];
}

/**
 * P3.4: the §6 trip fields a driver may write (the server's typed edge —
 * unknown fields are refused). Timestamps are RFC-3339 strings; odometers are
 * miles. `inbound_order_id`/`status` are server-owned and NOT sendable.
 */
export interface TripLogInput {
  schedule_date?: string;
  crew_size?: number;
  depart_facility_ts?: string;
  arrive_customer_ts?: string;
  depart_customer_ts?: string;
  arrive_facility_ts?: string;
  odometer_start?: number;
  odometer_dock?: number;
  odometer_return?: number;
  seal_no?: string;
  pallet_count?: number;
  notes?: string;
}

/**
 * P3.4: the driver's own trip log row (Driver redact projection) + the
 * SERVER-derived §6 figures. Costs NEVER appear on the driver surface — they
 * derive in the internal Erp-gated report only.
 */
export interface TripLogRecord {
  id: string;
  type: string;
  title: string | null;
  title_redacted: boolean;
  data: Record<string, unknown>;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  mileage: number | null;
  duration_hr: number | null;
}

export class DriverErpService {
  constructor(
    private getToken: () => string | null,
    /**
     * F007: invoked on a 401 (an expired/rejected access token) so the shell
     * can recover the session — a silent Keystore refresh grant on native, or
     * clearing the dead token so `AuthGuard` restarts PKCE login — instead of
     * every screen rendering the same generic "failed to load" a network blip
     * would show, with no way to force a fresh sign-in.
     */
    private readonly onAuthExpired?: () => void
  ) {}

  private async api<T>(path: string, init?: RequestInit): Promise<T> {
    const token = this.getToken();
    const res = await fetch(`/api${path}`, {
      headers: {
        accept: 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
      },
      ...init,
    });
    if (!res.ok) {
      if (res.status === 401) {
        this.onAuthExpired?.();
      }
      throw new Error(
        `driver api ${init?.method ?? 'GET'} ${path}: ${res.status}`
      );
    }
    return (await res.json()) as T;
  }

  listPickups(): Promise<DriverPickup[]> {
    return this.api<DriverPickup[]>('/driver/pickups');
  }

  pickupDetail(id: string): Promise<PickupDetail> {
    return this.api<PickupDetail>(`/driver/pickups/${id}`);
  }

  completePickup(
    id: string,
    body: PickupCompleteBody
  ): Promise<PickupCompleteResult> {
    return this.api<PickupCompleteResult>(`/driver/pickups/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** P3.4: the driver's own trip log for one pickup (`null` before first save). */
  getTrip(orderId: string): Promise<{ trip: TripLogRecord | null }> {
    return this.api<{ trip: TripLogRecord | null }>(
      `/driver/pickups/${orderId}/trip`
    );
  }

  /** P3.4: upsert the driver's own trip log (one row per order per driver). */
  saveTrip(orderId: string, body: TripLogInput): Promise<TripLogRecord> {
    return this.api<TripLogRecord>(`/driver/pickups/${orderId}/trip`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }
}
